import { Button, Grid } from "@mui/material";
import { revalidateLogic } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";

import { useUserContext } from "@/contexts/UserContext/index.context";

import { step0Schema } from "../form-config";
import { useClientMandataireAutofill } from "../hooks/useClientMandataireAutofill";
import { ClientSection } from "../sections/ClientSection";
import { MandataireSection } from "../sections/MandataireSection";
import { MissionDateSection } from "../sections/MissionDateSection";
import type { DeliveryFormType } from "../types";
import { scrollToFirstError } from "../utils/scrollToFirstError";

interface Props {
  form: DeliveryFormType;
  isAdmin: boolean;
  onStepValid: () => void;
  disablePast?: boolean;
}

export function StepClientMandataire({ form, isAdmin, onStepValid, disablePast = true }: Props) {
  const { t } = useTranslation();
  const { userState } = useUserContext();
  const settings = userState?.settings;
  const oneDate = Boolean(settings?.one_date);

  // Clients: mount + autofill mandataire fields (hidden UI) so step0Schema can pass
  const { isReady: isMandataireReady } = useClientMandataireAutofill(form);

  return (
    <form.FormGroup
      name="step0"
      validators={{ onDynamic: step0Schema }}
      validationLogic={revalidateLogic()}
      onGroupSubmit={() => onStepValid()}
      onGroupSubmitInvalid={scrollToFirstError}
    >
      {(formGroup) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            formGroup.handleSubmit();
          }}
        >
          <Grid container spacing={4}>
            <Grid size={{ lg: isAdmin ? 6 : 12, xs: 12 }}>
              <ClientSection form={form} />
            </Grid>

            {/* Always mount so FormGroup can distribute zod errors onto fields.
                Clients keep it visually hidden (same as DeliveryFormV2). */}
            <Grid
              size={{ lg: 6, xs: 12 }}
              sx={{ display: isAdmin ? undefined : "none" }}
              aria-hidden={!isAdmin}
            >
              <MandataireSection form={form} isAdmin={isAdmin} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <MissionDateSection form={form} oneDate={oneDate} disablePast={disablePast} />
            </Grid>

            {/* Navigation buttons */}
            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
              <Grid container spacing={1}>
                <Grid size={{ md: 6, xs: 12 }}>
                  <Button color="secondary" disabled fullWidth variant="contained">
                    {t("common.navigation.previous")}
                  </Button>
                </Grid>
                <Grid size={{ md: 6, xs: 12 }}>
                  <Button
                    color="primary"
                    disabled={!isMandataireReady}
                    fullWidth
                    type="submit"
                    variant="contained"
                  >
                    {t("common.navigation.next")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
    </form.FormGroup>
  );
}

export default StepClientMandataire;
